/**
 * LinkSecurity.ts
 * Shared utility for safe link detection, validation, and sanitization in chat messages.
 * Prevents phishing/malicious URLs while allowing clickable links on trusted domains.
 */

// ─── Trusted domain whitelist ────────────────────────────────────────────────
const ALLOWED_DOMAINS: string[] = [
  // Video conferencing
  "meet.google.com",
  "teams.microsoft.com",
  "teams.live.com",
  "zoom.us",
  "webex.com",
  // NoLegalPaisa owned
  "nolegalpaisa.com",
  "qa.nolegalpaisa.com",
  // Common legal / court resources
  "ecourts.gov.in",
  "njdg.ecourts.gov.in",
  "sci.gov.in",
  "india.gov.in",
];

// ─── Known malicious / banned shorteners ─────────────────────────────────────
const BLOCKED_DOMAINS: string[] = [
  "bit.ly",
  "tinyurl.com",
  "t.co",
  "goo.gl",
  "ow.ly",
  "is.gd",
  "cutt.ly",
  "rebrand.ly",
  "shorturl.at",
];

// Params considered tracking noise – strip them
const TRACKING_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "fbclid", "gclid", "ref", "source"];

/**
 * Extracts the hostname from a URL string (lower-cased, no port).
 */
function extractHostname(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
}

/**
 * Returns true if the hostname matches an allowed domain or its subdomain.
 */
function isDomainAllowed(hostname: string): boolean {
  return ALLOWED_DOMAINS.some(
    (allowed) => hostname === allowed || hostname.endsWith(`.${allowed}`)
  );
}

/**
 * Returns true if the hostname is a known blocked/shortener domain.
 */
function isDomainBlocked(hostname: string): boolean {
  return BLOCKED_DOMAINS.some(
    (blocked) => hostname === blocked || hostname.endsWith(`.${blocked}`)
  );
}

/**
 * Strips tracking query parameters from a URL and enforces https.
 * Returns null if the URL is unsafe / not on the whitelist.
 */
export function sanitizeUrl(rawUrl: string): string | null {
  // Must start with https://
  if (!rawUrl.startsWith("https://")) return null;

  const hostname = extractHostname(rawUrl);
  if (!hostname) return null;
  if (isDomainBlocked(hostname)) return null;
  if (!isDomainAllowed(hostname)) return null;

  try {
    const parsed = new URL(rawUrl);
    TRACKING_PARAMS.forEach((p) => parsed.searchParams.delete(p));
    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * Segment produced by parseMessageWithLinks.
 * type "text"  → plain string, render as-is
 * type "link"  → safe, clickable URL
 * type "blocked" → unsafe URL, show warning badge instead
 */
export interface MessageSegment {
  type: "text" | "link" | "blocked";
  content: string;  // text value or cleaned url
  rawUrl?: string;  // original url before sanitisation (for blocked case)
}

// Regex that matches http:// and https:// URLs
const URL_REGEX = /https?:\/\/[^\s<>"']+/gi;

/**
 * Splits a message string into safe text / link / blocked segments.
 * Use this to render messages with clickable (and security-checked) links.
 */
export function parseMessageWithLinks(text: string): MessageSegment[] {
  if (!text) return [{ type: "text", content: "" }];

  const segments: MessageSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  URL_REGEX.lastIndex = 0; // reset stateful regex
  while ((match = URL_REGEX.exec(text)) !== null) {
    // Push any plain text before this match
    if (match.index > lastIndex) {
      segments.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }

    const rawUrl = match[0];
    const safeUrl = sanitizeUrl(rawUrl);

    if (safeUrl) {
      segments.push({ type: "link", content: safeUrl, rawUrl });
    } else {
      // URL found but NOT on whitelist → show security warning, don't link
      segments.push({ type: "blocked", content: "[Link blocked – untrusted domain]", rawUrl });
    }

    lastIndex = match.index + rawUrl.length;
  }

  // Remaining plain text
  if (lastIndex < text.length) {
    segments.push({ type: "text", content: text.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: "text", content: text }];
}
